import { useState } from 'react';
import { formatDate } from '../../../lib/format';
import type { SettlementStatus } from '@dru/types';
import { BRAND, formatPaise } from '@dru/types';
import {
  Alert, DataTable, Detail, DetailSection, Drawer, EmptyState, KeyFigures, StatusBadge, type Tone,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { useAuth } from '../../../lib/auth';
import { PageHeader } from '../../../layouts/ConsoleShell';
import { periodLabel } from '../../hr/lib/billing-view';
import { VenueSections } from '../components/VenueSections';

export interface OwnStatementRow {
  id: string;
  reference: string;
  periodStart: string;
  periodEnd: string;
  status: SettlementStatus | string;
  currency: string;
  subtotalPaise: number;
  paidPaise: number;
  outstandingPaise: number;
  sourceCount: number;
  unpricedCount: number;
  issuedAt: string | null;
  notes: string | null;
}

/**
 * A statement's state, as the payee reads it. "Issued" is the platform's
 * word for a statement sent and not yet paid; to the venue that means it is
 * waiting to be paid, so that is what it says.
 */
export const STATEMENT_STATE: Record<string, { label: string; tone: Tone }> = {
  DRAFT: { label: 'Being prepared', tone: 'pending' },
  ISSUED: { label: 'Awaiting payment', tone: 'dueSoon' },
  PARTIALLY_PAID: { label: 'Part paid', tone: 'dueSoon' },
  PAID: { label: 'Paid', tone: 'covered' },
  VOID: { label: 'Cancelled', tone: 'neutral' },
};
export const statementState = (s: string) => STATEMENT_STATE[s] ?? { label: s.replace(/_/g, ' ').toLowerCase(), tone: 'neutral' as Tone };

/**
 * What this venue has been paid, and what it is still owed.
 *
 * A settlement, not a staff payment. The distinction is the whole reason this
 * screen is its own rather than the one the physician and the pathologist
 * share: that ledger pays a named clinician for a sign-off they personally
 * made, and a hospital is an organisation. It is paid once for a period,
 * across every company it served — which is the same fact that gives it one
 * login.
 *
 * Read-only, and it has to be. A payee agreeing their own statement is not a
 * statement; closing a period, issuing it and recording a payment against it
 * are the platform's acts, and the ledger is append-only underneath.
 */
export function Earnings() {
  const { principal } = useAuth();
  const venue = principal?.partner;
  const [open, setOpen] = useState<OwnStatementRow | null>(null);
  const [show, setShow] = useState<'' | 'owed' | 'paid'>('');

  const statements = useQuery<{ items: OwnStatementRow[] } | null>(
    () => (venue ? api.get('/settlements/mine') : Promise.resolve(null)), [!!venue],
  );

  if (!venue) {
    return (
      <div className="space-y-4">
        <PageHeader className="mb-0" title="Earnings" />
        <EmptyState
          title="Earnings belong to a venue account"
          hint="This screen shows a hospital’s or laboratory’s settlement statements. Your account is not linked to a venue."
        />
      </div>
    );
  }

  const items = statements.data?.items ?? [];
  const live = items.filter((s) => s.status !== 'VOID' && s.status !== 'DRAFT');
  const outstanding = live.reduce((n, s) => n + s.outstandingPaise, 0);
  const settled = items.filter((s) => s.status !== 'VOID').reduce((n, s) => n + s.paidPaise, 0);
  const waiting = live.filter((s) => s.outstandingPaise > 0).length;
  // Money still owed first, then the newest; a filter for each half.
  const owedFirst = [...items].sort((a, b) =>
    Number(b.outstandingPaise > 0 && b.status !== 'VOID') - Number(a.outstandingPaise > 0 && a.status !== 'VOID')
    || b.periodEnd.localeCompare(a.periodEnd));
  const shown = owedFirst.filter((s) => !show
    || (show === 'owed' ? s.outstandingPaise > 0 && s.status !== 'VOID' && s.status !== 'DRAFT' : s.status === 'PAID'));
  const paidCount = items.filter((s) => s.status === 'PAID').length;

  return (
    <div className="space-y-4">
      <PageHeader
        className="mb-0"
        title="Earnings"
        hint={`${venue.name} · one statement per period, covering every company whose employees you saw in it. Statements are prepared and paid by ${BRAND.name}.`}
      />
      <VenueSections />

      <KeyFigures
        label="Your account"
        figures={[
          { key: 'out', label: 'Owed to you', value: formatPaise(outstanding), loading: statements.loading,
            tone: outstanding > 0 ? 'dueSoon' : undefined,
            hint: waiting ? `${waiting} statement${waiting === 1 ? '' : 's'} awaiting payment` : 'Nothing awaiting payment' },
          { key: 'paid', label: 'Paid to date', value: formatPaise(settled), loading: statements.loading, hint: `Recorded by ${BRAND.name}` },
          { key: 'n', label: 'Statements', value: String(items.length), loading: statements.loading },
        ]}
      />

      {items.length > 0 && (
        <div role="group" aria-label="Show statements" className="flex flex-wrap gap-1.5">
          {([['', 'All', items.length], ['owed', 'Awaiting payment', waiting], ['paid', 'Paid', paidCount]] as const).map(([k, label, n]) => (
            <button key={k || 'all'} type="button" aria-pressed={show === k} onClick={() => setShow(k)}
              className={`inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-small font-medium transition-colors max-md:h-10 ${show === k ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50'}`}>
              {label}<span className="tabular-nums text-neutral-500">{n}</span>
            </button>
          ))}
        </div>
      )}

      <DataTable<OwnStatementRow>
        rows={shown}
        loading={statements.loading}
        error={statements.error}
        onRetry={statements.reload}
        rowKey={(s) => s.id}
        onRowClick={(s) => setOpen(s)}
        empty={show ? {
          title: show === 'owed' ? 'Nothing awaiting payment' : 'No paid statements yet',
          hint: 'Show all statements to see the rest.',
        } : {
          title: 'No statements yet',
          hint: `A statement appears here once ${BRAND.name} closes a period that included work done at your venue — usually monthly.`,
        }}
        columns={[
          {
            key: 'period', header: 'Period', alwaysVisible: true,
            cell: (s) => (
              <div>
                <div className="whitespace-nowrap text-sm font-medium text-neutral-900">{periodLabel(s.periodStart, s.periodEnd)}</div>
                <div className="font-mono text-caption text-neutral-500">{s.reference}</div>
              </div>
            ),
          },
          {
            key: 'work', header: 'Items', align: 'right',
            cell: (s) => (
              <div className="text-small tabular-nums text-neutral-700">
                {s.sourceCount}
                {s.unpricedCount > 0 && (
                  <span className="block text-caption text-warning-600">{s.unpricedCount} not priced</span>
                )}
              </div>
            ),
          },
          {
            key: 'amount', header: 'Amount', align: 'right',
            cell: (s) => <span className="whitespace-nowrap text-sm tabular-nums text-neutral-900">{formatPaise(s.subtotalPaise, s.currency)}</span>,
          },
          {
            key: 'paid', header: 'Paid', align: 'right', hideOnMobile: true,
            cell: (s) => <span className="whitespace-nowrap text-sm tabular-nums text-neutral-700">{formatPaise(s.paidPaise, s.currency)}</span>,
          },
          {
            key: 'out', header: 'Outstanding', align: 'right',
            cell: (s) => (
              <span className={`whitespace-nowrap text-sm tabular-nums ${s.outstandingPaise > 0 ? 'font-semibold text-neutral-900' : 'text-neutral-500'}`}>
                {formatPaise(s.outstandingPaise, s.currency)}
              </span>
            ),
          },
          {
            key: 'status', header: 'Status', mobileLabel: false,
            cell: (s) => <StatusBadge tone={statementState(s.status).tone}>{statementState(s.status).label}</StatusBadge>,
          },
        ]}
      />

      {open && (
        <Drawer title={`Statement ${open.reference}`} onClose={() => setOpen(null)}>
          <div className="space-y-4">
            <DetailSection title="Period">
              <div className="grid gap-4 sm:grid-cols-2">
                <Detail label="Covers" value={periodLabel(open.periodStart, open.periodEnd)} />
                <Detail label="Issued" value={open.issuedAt ? formatDate(open.issuedAt) : 'Not issued yet'} />
                <Detail label="Items" value={String(open.sourceCount)} />
                <Detail label="Status" value={<StatusBadge tone={statementState(open.status).tone}>{statementState(open.status).label}</StatusBadge>} />
              </div>
            </DetailSection>

            <DetailSection title="Amounts">
              <dl className="space-y-1 text-small">
                <div className="flex justify-between gap-3 text-neutral-700"><dt>Statement amount</dt><dd className="tabular-nums">{formatPaise(open.subtotalPaise, open.currency)}</dd></div>
                <div className="flex justify-between gap-3 text-neutral-700"><dt>Paid</dt><dd className="tabular-nums">− {formatPaise(open.paidPaise, open.currency)}</dd></div>
                <div className="flex justify-between gap-3 border-t border-neutral-200 pt-1 font-medium text-neutral-900"><dt>Outstanding</dt><dd className="tabular-nums">{formatPaise(open.outstandingPaise, open.currency)}</dd></div>
              </dl>
              {open.unpricedCount > 0 && (
                <Alert tone="warning" className="mt-3">
                  {open.unpricedCount} item{open.unpricedCount === 1 ? '' : 's'} in this period had no
                  agreed rate and {open.unpricedCount === 1 ? 'is' : 'are'} not in the amount. Ask {BRAND.name} support to price
                  {open.unpricedCount === 1 ? ' it' : ' them'}; the period can then be reissued.
                </Alert>
              )}
            </DetailSection>

            {open.notes && (
              <DetailSection title={`Note from ${BRAND.name}`}>
                <p className="text-small text-neutral-700">{open.notes}</p>
              </DetailSection>
            )}
          </div>
        </Drawer>
      )}
    </div>
  );
}
